import React, { useRef, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import CircleQuestion from 'components/application/CircleQuestion';
import { DropdownIndicator } from 'components/application/CollectionSelect';
import Tooltip from 'components/application/Tooltip';

function ManagerOnlySublabel({ name }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'teams.SelectMembers.ManagerOnlySublabel',
  });

  const { t: i18nTTeams } = useTranslation('common', {
    keyPrefix: 'teams',
  });

  return (
    <div className='items-center tw-flex'>
      <span>{i18nT('text')}</span>
      <div className='tw-ml-2'>
        <Tooltip
          placement='top'
          tooltip={i18nTTeams('cantBeMadeMember', {
            interpolation: { escapeValue: false },
            name,
          })}
          trigger='hover'
        >
          <CircleQuestion size='sm' />
        </Tooltip>
      </div>
    </div>
  );
}

const getNextPageParam = (lastPage, allPages) => {
  const { currentPage, totalPages } = lastPage.meta;

  return currentPage !== totalPages ? allPages.length + 1 : undefined;
};

export default function SelectMembers({ onTeamMemberSelect, team }) {
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [valueMenuOpen, setValueMenuOpen] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'teams.SelectMembers',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handleDebounce = (searchText) => {
    setSearchText(searchText);
    setIsDebouncing(false);
  };

  const debounceInput = useRef(
    _.debounce((searchText) => handleDebounce(searchText), 500),
  ).current;

  const setSearchTextDebounced = (searchText) => {
    if (searchText && searchText.length > 0) {
      setIsDebouncing(true);
    }
    debounceInput(searchText);
  };

  const assignableMembersParams = {
    search: searchText,
    filter: { ids: { nin: team.members.map((member) => member.id) } },
    company_id: team.companyId,
  };

  const handleMenuOpen = () => setValueMenuOpen(true);
  const handleMenuClose = () => setValueMenuOpen(false);

  const fetchAssignableMembers = (page) =>
    axios.post('/assignable_team_member_searches', {
      assignable_team_member_search: {
        ...assignableMembersParams,
        page,
      },
    });

  const {
    data: assignableMembers,
    fetchNextPage: fetchNextAssignableMembersPage,
    hasNextPage: assignableMembersHasNextPage,
    isFetching: assignableMembersIsFetching,
  } = useInfiniteQuery({
    queryKey: ['assignableMembers', assignableMembersParams],
    queryFn: async ({ pageParam = 1 }) => {
      const assignableMembersResponse = await fetchAssignableMembers(pageParam);
      return assignableMembersResponse.data;
    },
    getNextPageParam,
    enabled: valueMenuOpen,
  });

  const assignableOptions = (resourceData) => {
    const fullAssignableMembers = resourceData.pages
      .map((page) => page.data)
      .flat();
    const membersList = fullAssignableMembers.map((member) => {
      const managerOnly = member.relationships.personnel.data === null;
      return {
        value: {
          id: member.id,
          userId:
            member.relationships.user.data && member.relationships.user.data.id,
          personnelId:
            member.relationships.personnel.data &&
            member.relationships.personnel.data.id,
          isManager: managerOnly,
        },
        label: `${member.attributes.firstName} ${member.attributes.lastName}`,
        sublabel:
          managerOnly ?
            <ManagerOnlySublabel name={member.attributes.firstName} />
          : '',
      };
    });
    return membersList;
  };

  return (
    <>
      <label
        className='collection-select__label tw-font-medium'
        htmlFor='memberIds'
      >
        {i18nT('label')}
      </label>
      <Select
        className='collection-select__select-container tw-mb-4'
        classNamePrefix='collection-select'
        components={{ DropdownIndicator, Option }}
        id='memberIds'
        isLoading={isDebouncing || assignableMembersIsFetching}
        loadingMessage={() => i18nTShared('loading...')}
        name='memberIds'
        noOptionsMessage={() =>
          searchText ? i18nTShared('noMatchingResults') : null
        }
        onChange={(e) => onTeamMemberSelect(e.value)}
        onInputChange={setSearchTextDebounced}
        onMenuClose={handleMenuClose}
        onMenuOpen={handleMenuOpen}
        onMenuScrollToBottom={() => {
          if (assignableMembersHasNextPage) fetchNextAssignableMembersPage();
        }}
        openMenuOnFocus={true}
        options={
          assignableMembers && !isDebouncing ?
            assignableOptions(assignableMembers)
          : []
        }
        placeholder={i18nTShared('search...')}
        value={null}
      />
    </>
  );
}

export function Option(props) {
  return (
    <components.Option {...props}>
      <div className='collection-select__option_container'>
        <span>{props.children}</span>
        {props.data.sublabel && (
          <div className='truncated-text-container collection-select-option__subtext'>
            {props.data.sublabel}
          </div>
        )}
      </div>
    </components.Option>
  );
}

ManagerOnlySublabel.propTypes = {
  name: PropTypes.string.isRequired,
};

SelectMembers.propTypes = {
  team: PropTypes.object.isRequired,
  onTeamMemberSelect: PropTypes.func.isRequired,
};
